export interface MissingFieldItem {
  sectionName: string;
  groupName: string;
  fieldLabel: string;
  fieldTemplateId: number;
  // ordering for modal grouping
  sectionPageOrder?: number;
  groupDisplayOrder?: number;
}

export interface MissingFieldsInput {
  template: any;
  valuesByFieldId: { [fieldTemplateId: number]: any };
}

export interface MissingFieldGroup {
  key: string;
  sectionName: string;
  groupName: string;
  fields: MissingFieldItem[];
  sectionPageOrder?: number;
  groupDisplayOrder?: number;
}

export function buildMissingFields(input: MissingFieldsInput): MissingFieldItem[] {
  const template = input?.template;
  const valuesByFieldId = input?.valuesByFieldId ?? {};

  const items: MissingFieldItem[] = [];

  const sections = template?.sections ?? [];
  for (const s of sections) {
    const groups = s?.groups ?? [];
    for (const g of groups) {
      if (!g?.usesFieldTemplates) { continue; }

      const fields = g?.fields ?? [];
      for (const f of fields) {
        const stored = valuesByFieldId[f.id];
        if (isFieldMissing(f, stored)) {
          items.push({
            sectionName: s.name || s.title || '',
            groupName: g.name || g.title || '',
            fieldLabel: f.label || '',
            fieldTemplateId: f.id,
            sectionPageOrder: g.sectionPageOrder,
            groupDisplayOrder: g.displayOrder
          });
        }
      }
    }
  }

  return items;
}

function isFieldMissing(field: any, stored: any): boolean {
  // computed fields should not block completion
  if (field?.dataKey) { return false; }

  // readOnly fields should not block completion
  if (field?.readOnly) { return false; }

  // if there is no stored value at all, it's missing
  if (!stored) { return true; }

  switch (field?.dataTypeId) {
    case 1: { // string
      const v = (stored.stringValue ?? stored.StringValue ?? '').toString();
      return v.trim().length === 0;
    }
    case 2: // int
      return (stored.intValue ?? stored.IntValue) == null;
    case 3: // decimal
      return (stored.decimalValue ?? stored.DecimalValue) == null;
    case 4: // datetime
    case 5: // time
      return (stored.dateTimeValue ?? stored.DateTimeValue) == null;
    case 6: // dropdown
      return (stored.intValue ?? stored.IntValue) == null;
    case 7: { // checkbox
      const v = (stored.boolValue ?? stored.BoolValue);
      return v == null; // false is valid, null means untouched
    }
    default:
      return false;
  }
}

export interface MissingRule<T> {
  label: string;
  get: (m: T) => any;
  required?: (m: T) => boolean; // optional conditional
}

export function buildMissingFromRules<T>(
  model: T,
  rules: Array<MissingRule<T>>,
  mk: (label: string) => MissingFieldItem
): MissingFieldItem[] {
  const empty = (v: any) => v == null || (typeof v === 'string' && v.trim().length === 0);

  const items: MissingFieldItem[] = [];
  for (const r of rules) {
    if (r.required && !r.required(model)) {
      continue;
    }

    const v = r.get(model);
    if (empty(v)) {
      items.push(mk(r.label));
    }
  }

  return items;
}




// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/helpers/jacking-missing-fields.helper.ts